import type { IPicture } from '@quesmed/types/models';
import type { JSX } from 'react';
import { useState } from 'react';
import { BsEye, BsEyeSlash } from 'react-icons/bs';
import ReactMarkdown from 'react-markdown';
import rehypeExternalLinks from 'rehype-external-links';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { cn } from '../../cn';
import { Button } from '../Button/Button';

const CustomImage = ({
  className,
  wrapperClassName,
  picture,
  src,
}: {
  className?: string;
  wrapperClassName?: string;
  picture: IPicture;
  src?: string;
}) => {
  const [isBlurred, setIsBlurred] = useState(picture?.sensitive ?? false);

  if (!picture) {
    return (
      <img
        src={'/assets/logo.jpg'}
        alt='alternate'
        className={cn('max-w-full h-auto rounded-lg my-4', className)}
      />
    );
  }

  const handleToggleBlur = () => {
    setIsBlurred((prev) => !prev);
  };

  return (
    <div
      className={cn(
        'relative flex flex-col overflow-hidden justify-center items-center w-full h-full',
        wrapperClassName,
      )}
    >
      {/* <a href={picture.path}> */}
      <div
        className={cn(
          'relative',
          // 'rounded-lg object-contain object-center h-44 w-full',
          'rounded-lg bg-default-500/20 border border-default-100 p-2',
        )}
      >
        <img
          src={src || picture.path}
          alt={picture.caption || picture.name}
          data-sub-html={picture.caption}
          className={cn(
            'max-w-full rounded-lg w-120 max-h-120 object-contain object-center',
            className,
            // picture.sensitive && isBlurred ? 'blur-2xl' : '',
          )}
          style={picture.sensitive && isBlurred ? { filter: 'blur(3em)' } : undefined}
        />
        {picture.sensitive && (
          <Button
            className='absolute top-2 right-2'
            isIconOnly
            variant='light'
            size='sm'
            color='default'
            onPress={handleToggleBlur}
          >
            {isBlurred ? <BsEyeSlash /> : <BsEye />}
          </Button>
        )}
      </div>
      {/* </a> */}
      {picture.caption && <div className='text-center text-xs text-default-500 mt-1'>{picture.caption}</div>}
    </div>
  );
};

interface MarkdownProps {
  children: string;
  className?: string;
  classNames?: {
    h1?: string;
    h2?: string;
    h3?: string;
    h4?: string;
    h5?: string;
    h6?: string;
    p?: string;
    ul?: string;
    ol?: string;
    li?: string;
    blockquote?: string;
    a?: string;
    img?: string;
    imgWrapper?: string;
    table?: string;
    tableWrapper?: string;
    th?: string;
    td?: string;
    code?: string;
    pre?: string;
    hr?: string;
    tbody?: string;
  };
}

const Markdown = ({ children, className, classNames }: MarkdownProps): JSX.Element => (
  <ReactMarkdown
    components={{
      h1: ({ children }) => (
        <h1 className={cn('text-xl md:text-xl lg:text-2xl font-semibold my-6', classNames?.h1, className)}>
          {children}
        </h1>
      ),
      h2: ({ children }) => (
        <h2 className={cn('text-lg md:text-xl lg:text-2xl font-semibold my-3', classNames?.h2, className)}>
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 className={cn('text-lg md:text-xl lg:text-2xl font-medium my-2', classNames?.h3, className)}>
          {children}
        </h3>
      ),
      h4: ({ children }) => (
        <h4 className={cn('text-base md:text-lg lg:text-xl font-medium my-2', classNames?.h4, className)}>
          {children}
        </h4>
      ),
      h5: ({ children }) => (
        <h5 className={cn('text-sm md:text-base lg:text-lg font-medium my-2', classNames?.h5, className)}>
          {children}
        </h5>
      ),
      h6: ({ children }) => (
        <h6 className={cn('text-xs md:text-sm lg:text-base font-medium my-2', classNames?.h6, className)}>
          {children}
        </h6>
      ),
      p: ({ children }) => (
        <p className={cn('text-sm md:text-base leading-relaxed my-4', classNames?.p, className)}>
          {children}
        </p>
      ),
      ul: ({ children }) => (
        <ul className={cn('list-disc ml-4 my-2', classNames?.ul, className)}>{children}</ul>
      ),
      ol: ({ children }) => (
        <ol className={cn('list-decimal ml-4 my-2', classNames?.ol, className)}>{children}</ol>
      ),
      li: ({ children }) => (
        <li className={cn('my-1 text-sm md:text-base', classNames?.li, className)}>{children}</li>
      ),
      blockquote: ({ children }) => (
        <blockquote
          className={cn(
            'border-l-4 text-sm md:text-base border-default-300 pl-4 italic my-4',
            classNames?.blockquote,
            className,
          )}
        >
          {children}
        </blockquote>
      ),
      a: ({ href, children }) => (
        <a
          href={href}
          target='_blank'
          rel='noopener noreferrer'
          className={cn(
            'text-secondary-500 text-sm md:text-base hover:text-secondary-700 underline',
            classNames?.a,
            className,
          )}
        >
          {children}
        </a>
      ),
      img: ({ src, alt }) => {
        let metadata: any = null;
        if (alt) {
          try {
            metadata = JSON.parse(alt);
          } catch {
            // alt is not JSON, ignore
          }
        }

        if (metadata?.path) {
          return (
            <CustomImage
              src={src}
              className={classNames?.img}
              wrapperClassName={classNames?.imgWrapper}
              picture={metadata}
            />
          );
        }

        return (
          <img
            src={src}
            alt={alt}
            className={cn('max-w-full h-auto rounded-lg my-4', classNames?.img, className)}
          />
        );
      },
      table: ({ children }) => (
        <div className={cn('', classNames?.tableWrapper, className)}>
          <table
            className={cn('min-w-full overflow-x-auto border-collapse my-6', classNames?.table, className)}
          >
            {children}
          </table>
        </div>
      ),
      tbody: ({ children }) => <tbody className={cn('', classNames?.tbody, className)}>{children}</tbody>,
      th: ({ children }) => (
        <th
          className={cn(
            children
              ? 'text-left text-default-800 font-medium border border-default-200 bg-default-50 p-2'
              : '',
            classNames?.th,
            className,
          )}
        >
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className={cn('border text-default-600 border-default-200 p-2', classNames?.td, className)}>
          {children}
        </td>
      ),
      code: ({ children }) => <code className='bg-content2 p-2 rounded-lg text-xs'>{children}</code>,
      pre: ({ children }) => (
        <pre className={cn('bg-content2 p-4 rounded-lg overflow-x-auto my-4', classNames?.pre, className)}>
          {children}
        </pre>
      ),
      hr: () => <hr className={cn('border-t border-default-50 my-8', classNames?.hr, className)} />,
    }}
    rehypePlugins={[rehypeRaw, [rehypeExternalLinks, { target: '_blank' }]]}
    remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
    urlTransform={(url) => url}
  >
    {children}
  </ReactMarkdown>
);

export { Markdown };
